/**
 * Tier 2 Group F sub-piece #5 — Slice export pipeline.
 *
 * Given the canvas state and a list of named slice rectangles,
 * produces one rasterized file per slice in a single output:
 *
 *   - A ZIP bundle (default for local download) using JSZip 3.10.1.
 *   - An array of named `File` objects (consumed by the Fias-save
 *     loop in EditorPage, which uploads each through the existing
 *     `processImage` flow).
 *
 * The module is pure with respect to React state and the canvas
 * hook — callers pass in a `sourceCanvasProvider` thunk that
 * returns a same-pixel-density HTMLCanvasElement at the document's
 * native resolution, and a normalized slice list. The cropping +
 * scale composition is mechanical: drawImage with explicit
 * source / destination rects.
 *
 * Filenames are derived from `slugifySliceName`, which lowercases,
 * strips OS-illegal characters, and disambiguates duplicates with
 * an `_2`, `_3`, … suffix. The output extension is taken from the
 * format string.
 */

import JSZip from 'jszip';
import { encodeCanvasAsPdfBlob } from './pdf-export';
import type { CanvasNativeExportFormat, SliceRect } from '../types';

/** Formats supported by the slice export pipeline. Canvas-native
 *  rasters (PNG / JPEG / WebP / AVIF) go through `canvas.toBlob`;
 *  TIFF goes through the bundled utif encoder; PDF goes through the
 *  jsPDF encoder (one single-page document per slice). PSD / SVG
 *  are NOT in this set — they carry layered structure that doesn't
 *  decompose into per-region files. See `isSlicesIncompatibleFormat`
 *  in `export-formats.ts` for the dialog-side gate.
 *
 *  Structurally `SliceExportFormat` equals `CanvasNativeExportFormat |
 *  'pdf'` — TIFF participates because it's already in
 *  `CanvasNativeExportFormat` (single-raster format with a library
 *  encoder, same conceptual shape as PNG/JPEG). */
export type SliceExportFormat = CanvasNativeExportFormat | 'pdf';

/** Output options for a slice export run. The `scaleMultiplier`
 *  applies uniformly to every slice (matches the ExportDialog's
 *  1x/2x/3x Scale radio — Custom dimensions are not applicable
 *  to slices because each slice has its own natural pixel size).
 *  `quality` is the canvas-native encoder quality 0..1 (PNG
 *  ignores it; JPEG / WebP use the curve; PDF embeds via JPEG
 *  internally with its own fixed quality and ignores this). */
export interface SliceExportOptions {
  format: SliceExportFormat;
  /** Pass 92 → 0.92, matching the ExportDialog UI convention.
   *  Callers should pre-normalize before invoking. */
  quality: number;
  scaleMultiplier: number;
}

/** Slugify a slice name into a filesystem-safe stem. Lowercases,
 *  collapses whitespace runs into single `_`, strips characters
 *  that Windows / macOS reject in filenames (`/`, `\`, `:`, `*`,
 *  `?`, `"`, `<`, `>`, `|`), and ASCII-folds nothing — the
 *  output is the user's input minus illegal chars, not a
 *  transliteration. Empty (or pure-whitespace) names fall back
 *  to "slice".
 *
 *  Disambiguation against a Set of already-used stems is handled
 *  by the caller via the second-argument helper below — keeps
 *  this function pure for the unit tests. */
/** Windows reserved device names (case-insensitive). A file with
 *  any of these stems — regardless of extension — fails to create
 *  on Windows. The set covers DOS legacy `CON` / `PRN` / `AUX` /
 *  `NUL` plus the `COM1..COM9` / `LPT1..LPT9` serial-printer
 *  reserves. Microsoft also reserves `CONIN$` / `CONOUT$` for
 *  Windows-NT consoles but those aren't valid identifiers users
 *  would type by accident. */
const WINDOWS_RESERVED_NAMES = new Set([
  'con',
  'prn',
  'aux',
  'nul',
  'com1',
  'com2',
  'com3',
  'com4',
  'com5',
  'com6',
  'com7',
  'com8',
  'com9',
  'lpt1',
  'lpt2',
  'lpt3',
  'lpt4',
  'lpt5',
  'lpt6',
  'lpt7',
  'lpt8',
  'lpt9',
]);

/** Maximum filename stem length. Cross-platform filesystems
 *  generally cap names at 255 BYTES (NTFS, ext4, APFS). Multi-byte
 *  UTF-8 characters can blow past this even at 100 code points, so
 *  we hard-cap the stem at 120 characters as a safe headroom that
 *  still leaves room for the extension + a `_NNN` disambiguator.
 *  Truncation happens after slugification + before the reserved-
 *  name check (the reserved set is short and lowercase so a 120-
 *  char prefix can't accidentally match). */
const MAX_SLICE_NAME_LENGTH = 120;

export function slugifySliceName(name: string): string {
  const trimmed = name.trim();
  if (!trimmed) return 'slice';
  // Preserve the user's case + spaces — filenames are end-user-
  // facing and a slice named "Hero Banner" should download as
  // `Hero Banner.png`, not `hero_banner.png`. We only strip
  // characters that are genuinely illegal on common filesystems:
  //
  //   1. Filesystem path / wildcard / quote metachars:
  //      `/` `\` `:` `*` `?` `"` `<` `>` `|`
  //   2. Control chars `\x00–\x1f` (NUL, DEL, etc) and `\x7f`,
  //      EXCLUDING `\t` and `\n` which would already be folded by
  //      `trim()`-friendly handling — we route them through the
  //      whitespace collapse below. Tabs in the middle of a name
  //      collapse to a single space.
  //   3. Multiple consecutive whitespace → single space.
  //   4. Leading dots (would create hidden files on Unix) +
  //      trailing dots / spaces (Windows rejects them).
  //
  // After cleanup: length cap (cross-platform 255-byte limit; we
  // cap at 120 chars headroom for the extension + disambiguator),
  // then Windows reserved-device-name protection (CON/PRN/etc →
  // prefix with `_`).
  let slug = trimmed
    // Collapse runs of whitespace (incl. tab / newline) to a
    // single regular space — keeps the visual "two-word" structure
    // of "Hero Banner" without leaving raw tabs / newlines in the
    // filename.
    .replace(/\s+/g, ' ')
    // Strip illegal filesystem metachars.
    .replace(/[/\\:*?"<>|]/g, '')
    // Strip control chars but NOT the regular space `\x20`. Keep
    // `\t` and `\n` out — they were collapsed to a space above.
    // eslint-disable-next-line no-control-regex
    .replace(/[\x00-\x1f\x7f]/g, '')
    // Strip leading dots (Unix hidden-file marker).
    .replace(/^\.+/, '')
    // Strip trailing dots and whitespace (Windows file-create
    // rejects them outright).
    .replace(/[.\s]+$/, '');
  if (!slug) return 'slice';
  if (slug.length > MAX_SLICE_NAME_LENGTH) {
    slug = slug.slice(0, MAX_SLICE_NAME_LENGTH).replace(/[.\s]+$/, '');
    if (!slug) return 'slice';
  }
  // Windows reserved device names match case-insensitive against
  // the BARE stem (`CON`, `Con`, `con` all collide). Prefix with
  // `_` so the user's intent is preserved in the output and the
  // file actually creates on Windows.
  if (WINDOWS_RESERVED_NAMES.has(slug.toLowerCase())) {
    return `_${slug}`;
  }
  return slug;
}

/** Disambiguate `stem` against `used` by appending `_2`, `_3`, …
 *  until a free name is found. The returned stem is added to
 *  `used` so subsequent calls produce monotonically-increasing
 *  suffixes for the next collision. */
export function ensureUniqueSliceFilename(stem: string, used: Set<string>): string {
  if (!used.has(stem)) {
    used.add(stem);
    return stem;
  }
  let n = 2;
  while (used.has(`${stem}_${n}`)) n++;
  const next = `${stem}_${n}`;
  used.add(next);
  return next;
}

/** MIME type for a canvas-native export format. Mirrors the
 *  `image/${format}` convention used by `HTMLCanvasElement.toBlob`. */
export function mimeTypeForFormat(format: CanvasNativeExportFormat): string {
  return `image/${format}`;
}

/** File extension for a slice export format. Equals the format
 *  string for our canvas-native formats (png / jpeg / webp / avif)
 *  and PDF; separated as a helper so a future format that wants a
 *  shorter / different extension (e.g. `'jpeg'` → `'jpg'`) has one
 *  place to map. */
export function fileExtensionForFormat(format: SliceExportFormat): string {
  return format;
}

/** Clamp a slice's scene-space rect to canvas bounds. Returns
 *  `null` when the intersection is empty (slice entirely off-
 *  canvas) — the export pipeline drops such slices rather than
 *  encoding zero-pixel files. */
export function clampSliceToCanvas(
  slice: Pick<SliceRect, 'x' | 'y' | 'width' | 'height'>,
  canvasWidth: number,
  canvasHeight: number,
): { x: number; y: number; width: number; height: number } | null {
  const x0 = Math.max(0, Math.round(slice.x));
  const y0 = Math.max(0, Math.round(slice.y));
  const x1 = Math.min(canvasWidth, Math.round(slice.x + slice.width));
  const y1 = Math.min(canvasHeight, Math.round(slice.y + slice.height));
  if (x1 <= x0 || y1 <= y0) return null;
  return { x: x0, y: y0, width: x1 - x0, height: y1 - y0 };
}

/** Crop a region of `source` and scale into a fresh canvas. The
 *  output canvas is sized to `clamped.width * scaleMultiplier`
 *  by `clamped.height * scaleMultiplier`, rounded to integer
 *  pixels. Image smoothing is high (matches the existing
 *  `resizeDataUrlToTarget` choice) so 2×/3× scaled raster looks
 *  reasonable without halos. */
export function cropCanvasToSlice(
  source: HTMLCanvasElement,
  clamped: { x: number; y: number; width: number; height: number },
  scaleMultiplier: number,
): HTMLCanvasElement {
  const outW = Math.max(1, Math.round(clamped.width * scaleMultiplier));
  const outH = Math.max(1, Math.round(clamped.height * scaleMultiplier));
  const out = document.createElement('canvas');
  out.width = outW;
  out.height = outH;
  const ctx = out.getContext('2d');
  // Without a 2d context the slice silently disappears from the
  // bundle. Mirror the existing resize path's behavior (it returns
  // null) — caller filters out null entries in `exportSlicesAsBlobs`.
  if (!ctx) return out;
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(source, clamped.x, clamped.y, clamped.width, clamped.height, 0, 0, outW, outH);
  return out;
}

/** Encode a canvas to a Blob via the canvas-native encoder.
 *  Promise-wrapped because `toBlob` is async and callers want
 *  to await the per-slice encode. Resolves null on encoder
 *  failure (rare; the caller filters). */
export function canvasToBlobAsync(
  canvas: HTMLCanvasElement,
  format: CanvasNativeExportFormat,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), mimeTypeForFormat(format), quality);
  });
}

/** Per-format dispatch — produces a single Blob for one slice.
 *  Browser-encoded formats (PNG / JPEG / WebP / AVIF) route to
 *  `canvas.toBlob` via the helper above; PDF routes to jsPDF via
 *  `encodeCanvasAsPdfBlob`; TIFF routes to utif via
 *  `encodeCanvasAsTiffBlob` because browsers don't natively encode
 *  TIFF. Returns null on encoder failure so the caller can treat
 *  per-slice failures the same way across formats. */
export async function encodeSliceCanvas(
  canvas: HTMLCanvasElement,
  format: SliceExportFormat,
  quality: number,
): Promise<Blob | null> {
  if (format === 'pdf') {
    try {
      return await encodeCanvasAsPdfBlob(canvas);
    } catch {
      return null;
    }
  }
  if (format === 'tiff') {
    try {
      const { encodeCanvasAsTiffBlob } = await import('./tiff-export');
      return await encodeCanvasAsTiffBlob(canvas);
    } catch {
      return null;
    }
  }
  // Explicit narrowing after the PDF + TIFF guards above. TS
  // structurally already infers this, but the named local makes
  // the intent obvious to a future reader looking at the
  // `canvas.toBlob` call signature. (Pre-PR review audit Critical #1.)
  const nativeFormat: Exclude<CanvasNativeExportFormat, 'tiff'> = format;
  return canvasToBlobAsync(canvas, nativeFormat, quality);
}

/** Encode + name every slice, returning an array of `{name, blob}`
 *  records the caller can stitch into a ZIP, sequential downloads,
 *  Fias uploads, or a `navigator.share({ files })` call. The
 *  filename includes its extension (e.g. `"hero.png"`) and is
 *  unique across the array.
 *
 *  Slices entirely outside the canvas bounds are skipped — they
 *  add no useful output. Slices whose encoder fails are also
 *  skipped (rare, but defensive). The returned `failed` count
 *  surfaces both reasons for the UI's "X of N succeeded" toast. */
export interface SliceExportResult {
  files: Array<{ name: string; blob: Blob }>;
  /** Slices that produced no output: out-of-canvas + encoder
   *  failures combined. Surface in a partial-failure toast. */
  failed: number;
  /** Total slices the caller passed in. `files.length + failed`. */
  total: number;
}

export async function exportSlicesAsBlobs(
  slices: readonly SliceRect[],
  source: HTMLCanvasElement,
  canvasWidth: number,
  canvasHeight: number,
  options: SliceExportOptions,
): Promise<SliceExportResult> {
  const used = new Set<string>();
  const files: Array<{ name: string; blob: Blob }> = [];
  let failed = 0;
  const ext = fileExtensionForFormat(options.format);
  for (const slice of slices) {
    const clamped = clampSliceToCanvas(slice, canvasWidth, canvasHeight);
    if (!clamped) {
      failed++;
      continue;
    }
    const sliceCanvas = cropCanvasToSlice(source, clamped, options.scaleMultiplier);
    const blob = await encodeSliceCanvas(sliceCanvas, options.format, options.quality);
    if (!blob) {
      failed++;
      continue;
    }
    const stem = ensureUniqueSliceFilename(slugifySliceName(slice.name), used);
    files.push({ name: `${stem}.${ext}`, blob });
  }
  return { files, failed, total: slices.length };
}

/** Bundle the slice files into a single ZIP. Used by the local
 *  download destination — one click yields one `.zip` instead of
 *  N sequential download prompts (which most browsers throttle
 *  or block as popups). The compression level is the JSZip
 *  default (DEFLATE); the constituent images are already
 *  compressed so re-deflating them is mostly metadata-stripping. */
export async function bundleSlicesIntoZip(
  files: ReadonlyArray<{ name: string; blob: Blob }>,
): Promise<Blob> {
  const zip = new JSZip();
  for (const file of files) {
    zip.file(file.name, file.blob);
  }
  return zip.generateAsync({ type: 'blob' });
}
